import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from 'axios';
import Nav from "./Nav";
import Footer from "./Footer";
import { HiShieldCheck, HiArrowRight, HiArrowLeft, HiRefresh } from "react-icons/hi";

function EnterCode() {
  const navigate = useNavigate();
  const BASE_URL = `${process.env.REACT_APP_API_URL || `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}`}/api/`;

  const [formData, setFormData] = useState({
    code: "",
  });
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(60);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    // Check if email is verified
    const isEmailVerified = localStorage.getItem("emailVerified");
    if (!isEmailVerified) {
      toast.error("Please enter your email first");
      navigate("/forgot-password");
    }
  }, [navigate]);

  // 60-second Resend Countdown Timer
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      setCanResend(false);
      timer = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleResendCode = async () => {
    if (!canResend) return;
    const userEmail = localStorage.getItem("userEmail");
    if (!userEmail) {
      toast.error("Email session expired. Please start over.");
      navigate("/forgot-password");
      return;
    }

    try {
      const response = await axios.post(`${BASE_URL}forgot-password`, { email: userEmail });
      toast.success(response.data.msg || "A new 6-digit code has been sent!");
      setResendCooldown(60);
    } catch (err) {
      const msg = err.response?.data?.msg || "Failed to resend code";
      toast.error(msg);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.code.trim()) {
      toast.error("Please enter the 6-digit verification code");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}verify-reset-code`, formData);

      if (response.status === 201 || response.data.success) {
        toast.success("Code verified successfully");
        navigate("/reset-password");
      } else {
        toast.error(response.data.error || "Invalid verification code");
      }
    } catch (error) {
      console.error("Error:", error.message);
      const errMsg = error.response?.data?.error || "Failed to verify reset code";
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Nav />

      <main className="flex-grow flex items-center justify-center p-4 pt-32 pb-20">
        <div className="w-full max-w-[450px] animate-in fade-in zoom-in duration-500">
          <div className="glass rounded-3xl p-8 md:p-10 shadow-2xl relative overflow-hidden">
            {/* Background Decoration */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-blue-600/10 rounded-full blur-3xl"></div>

            <div className="relative z-10">
              <div className="text-center mb-8">
                <span className="inline-block px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-[10px] font-black uppercase tracking-widest mb-2 border border-blue-100">
                  Security Validation
                </span>
                <h1 className="text-2xl font-bold text-slate-900 mb-1">
                  Enter 6-Digit Code
                </h1>
                <p className="text-slate-500 text-xs">
                  Check your email inbox for the 6-digit verification code. Valid for 10 minutes.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label
                    className="text-xs font-bold uppercase tracking-wider text-slate-700 ml-1"
                    htmlFor="code"
                  >
                    Verification Code
                  </label>
                  <div className="relative group">
                    <HiShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-500 text-2xl" />
                    <input
                      className="input-field pl-12 tracking-[8px] font-mono font-bold text-xl text-slate-800"
                      id="code"
                      type="text"
                      name="code"
                      maxLength="6"
                      value={formData.code}
                      autoFocus
                      onChange={handleChange}
                      placeholder="000000"
                      required
                    />
                  </div>
                </div>

                <button
                  className="btn-primary w-full py-3.5 mt-2 flex items-center justify-center gap-2 group relative overflow-hidden disabled:opacity-70 disabled:cursor-not-allowed"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <span>Verify Code</span>
                      <HiArrowRight className="group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 pt-6 border-t border-slate-200/60 text-center space-y-4">
                <div>
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={!canResend}
                    className={`text-xs font-bold inline-flex items-center gap-1 transition-colors ${
                      canResend ? "text-blue-600 hover:text-blue-800 cursor-pointer" : "text-slate-400 cursor-not-allowed"
                    }`}
                  >
                    <HiRefresh size={14} className={!canResend ? "" : ""} />
                    {canResend ? "Resend 6-Digit Code" : `Resend code in ${resendCooldown}s`}
                  </button>
                </div>

                <div>
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-blue-600 transition-colors"
                  >
                    <HiArrowLeft size={16} />
                    <span>Back to Login</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default EnterCode;
